import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { projects } from '../data/projects';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

export function Projects() {
  return (
    <>
      <SEO
        title="Projects"
        description="Representative MaytoMation projects including lead intake automation, quote generators, customer portals, dashboards, infrastructure audits, and network documentation."
      />
      <PageHero
        eyebrow="Projects"
        title="Practical systems for businesses that need clearer operations."
        body="These examples show how MaytoMation approaches software, automation, infrastructure, networking, hosting, and documentation work. Every project starts with the real workflow and ends with a supportable system."
        primary={{ label: 'Request a Project', href: '/contact' }}
        secondary={{ label: 'View Pricing', href: '/pricing' }}
        highlights={['Automation', 'Custom software', 'Infrastructure', 'Documentation']}
      />

      <Section>
        <SectionHeading
          eyebrow="Showcase"
          title="Representative project patterns."
          body="The examples below are written in the language of business outcomes: what was hard, what was built, what changed, and what technology supported it."
        />
        <div className="mt-12 grid gap-6">
          {projects.map((project, index) => (
            <Reveal key={project.title}>
              <article className="overflow-hidden rounded-lg border border-brand-border bg-white shadow-card">
                <div className="grid gap-0 lg:grid-cols-[.55fr_1fr]">
                  <div className="bg-brand-dark p-7 text-white sm:p-8">
                    <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-teal">
                      {project.category}
                    </p>
                    <h2 className="mt-4 font-display text-3xl leading-tight">{project.title}</h2>
                    <div className="mt-8 flex h-14 w-14 items-center justify-center rounded-md bg-brand-teal text-2xl font-extrabold text-brand-dark">
                      {index + 1}
                    </div>
                  </div>
                  <div className="grid gap-6 p-7 sm:p-8 lg:grid-cols-3">
                    <ProjectColumn label="Challenge" body={project.challenge} icon="message" />
                    <ProjectColumn label="Solution" body={project.solution} icon="workflow" />
                    <ProjectColumn label="Outcome" body={project.outcome} icon="check" />
                    <div className="lg:col-span-3">
                      <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-brand-teal">
                        Technologies and practices
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {project.technologies.map((technology) => (
                          <span
                            key={technology}
                            className="rounded-full border border-brand-border bg-brand-background px-3 py-1 text-xs font-bold text-brand-navy"
                          >
                            {technology}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading
          eyebrow="Project Quality"
          title="Every build includes practical ownership details."
          body="The visible app or workflow is only one part of the project. MaytoMation also considers the operational details that keep systems maintainable."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[
            ['Access and ownership', 'Clear notes for accounts, permissions, domains, hosting, and support contacts.'],
            ['Documentation', 'Runbooks, diagrams, workflow notes, configuration details, and handoff material.'],
            ['Support planning', 'Maintenance expectations, monitoring, backups, response model, and improvement cadence.'],
            ['Change readiness', 'A clear place for business rules, integrations, exceptions, and future enhancements.']
          ].map(([title, body]) => (
            <Reveal key={title}>
              <article className="h-full rounded-lg border border-brand-border bg-brand-background p-6 shadow-card">
                <Icon name="fileText" className="h-7 w-7 text-brand-teal" />
                <h2 className="mt-5 text-lg font-extrabold text-brand-dark">{title}</h2>
                <p className="mt-3 text-sm leading-7 text-brand-muted">{body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection title="Have a project that does not fit a clean category?" button="Describe the Problem" />
    </>
  );
}

function ProjectColumn({ label, body, icon }: { label: string; body: string; icon: string }) {
  return (
    <div>
      <Icon name={icon} className="h-6 w-6 text-brand-teal" />
      <h3 className="mt-4 text-sm font-extrabold uppercase tracking-[0.14em] text-brand-navy">{label}</h3>
      <p className="mt-3 text-sm leading-7 text-brand-muted">{body}</p>
    </div>
  );
}
